import { apiGet, apiPost, type QueryRecord } from './http-client';

export type OnboardingResponse = {
  need_personal_data_agreement: boolean;
  need_platform_user_rules: boolean;
  need_crm_agreement: boolean;
  need_profile: boolean;
  need_org_agreement: boolean;
  personal_data_agreement_text: string;
  personal_data_agreement_version: number;
  platform_user_rules_text: string;
  platform_user_rules_version: number;
  crm_agreement_text: string;
  crm_agreement_version: number;
  org_agreement_text: string;
  org_agreement_version: number;
  /** id строки `agreements` для POST `/api/v1/agreements/{id}/accept` (платформа). */
  personal_data_agreement_id?: number;
  platform_user_rules_id?: number;
  crm_agreement_id?: number;
  org_agreement_id?: number | null;
  is_client_of_org: boolean | null;
};

function postAcceptAgreement(
  agreementId: number,
  body: { accepted_version: number; organization_id?: number },
  accessToken: string
): Promise<void> {
  return apiPost<void>(`/api/v1/agreements/${agreementId}/accept`, body, { accessToken });
}

/**
 * Статус соглашений для гейта embed: GET `/api/v1/auth/me/agreements-status`
 * (тексты с плейсхолдерами, флаги need_*).
 */
export function getMeOnboarding(
  organizationId: number | null,
  accessToken: string
): Promise<OnboardingResponse> {
  const q: QueryRecord = {};
  if (organizationId != null) {
    q.organization_id = organizationId;
  }
  return apiGet<OnboardingResponse>(EMBED_AUTH_ONBOARDING_API.agreementsStatus, q, { accessToken });
}

export function acceptPersonalDataAgreement(
  agreementId: number | null | undefined,
  version: number,
  accessToken: string
): Promise<void> {
  if (agreementId != null && agreementId > 0) {
    return postAcceptAgreement(agreementId, { accepted_version: version }, accessToken);
  }
  return apiPost<void>(
    EMBED_AUTH_ONBOARDING_API.acceptPersonal,
    { accepted_version: version },
    { accessToken }
  );
}

export function acceptPlatformUserRulesAgreement(
  agreementId: number | null | undefined,
  version: number,
  accessToken: string
): Promise<void> {
  if (agreementId != null && agreementId > 0) {
    return postAcceptAgreement(agreementId, { accepted_version: version }, accessToken);
  }
  return apiPost<void>(
    EMBED_AUTH_ONBOARDING_API.acceptRules,
    { accepted_version: version },
    { accessToken }
  );
}

export function acceptCrmAgreement(
  agreementId: number | null | undefined,
  version: number,
  accessToken: string
): Promise<void> {
  if (agreementId != null && agreementId > 0) {
    return postAcceptAgreement(agreementId, { accepted_version: version }, accessToken);
  }
  return apiPost<void>(
    EMBED_AUTH_ONBOARDING_API.acceptCrm,
    { accepted_version: version },
    { accessToken }
  );
}

export function acceptOrgAgreement(
  agreementId: number | null | undefined,
  organizationId: number,
  version: number,
  accessToken: string
): Promise<void> {
  if (agreementId != null && agreementId > 0) {
    return postAcceptAgreement(
      agreementId,
      { accepted_version: version, organization_id: organizationId },
      accessToken
    );
  }
  return apiPost<void>(
    EMBED_AUTH_ONBOARDING_API.acceptOrg,
    { organization_id: organizationId, accepted_version: version },
    { accessToken }
  );
}

export function onboardingPending(o: OnboardingResponse): boolean {
  return (
    o.need_personal_data_agreement ||
    o.need_platform_user_rules ||
    o.need_crm_agreement ||
    o.need_profile ||
    o.need_org_agreement
  );
}

export const EMBED_AUTH_ONBOARDING_API = {
  agreementsStatus: '/api/v1/auth/me/agreements-status',
  acceptPersonal: '/api/v1/auth/me/accept-personal-data-agreement',
  acceptRules: '/api/v1/auth/me/accept-platform-user-rules-agreement',
  acceptCrm: '/api/v1/auth/me/accept-crm-agreement',
  acceptOrg: '/api/v1/auth/me/accept-org-agreement'
} as const;
